import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RinkDiagram } from "../components/RinkDiagram";
import type { Drill } from "../domain/drill";
import { loadDrills } from "../lib/drill-storage";

const STORAGE_KEY = "puckly-coach-practice-plan-v1";

type PracticeStation = {
  drillId: string;
  title: string;
  durationMinutes: number;
  description: string;
  focus: string;
};

type PracticePlan = {
  team: string;
  date: string;
  startTime: string;
  theme: string;
  equipment: string;
  stations: PracticeStation[];
};

const emptyStation = (index: number): PracticeStation => ({
  drillId: "",
  title: `Station ${index + 1}`,
  durationMinutes: 15,
  description: "",
  focus: "",
});

const emptyPlan = (): PracticePlan => ({
  team: "Puckly practice",
  date: new Date().toISOString().slice(0, 10),
  startTime: "",
  theme: "",
  equipment: "",
  stations: [emptyStation(0), emptyStation(1), emptyStation(2)],
});

function loadPlan(): PracticePlan {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return emptyPlan();
    const parsed = JSON.parse(saved) as Partial<PracticePlan>;
    return {
      ...emptyPlan(),
      ...parsed,
      stations: Array.from({ length: 3 }, (_, index) => ({
        ...emptyStation(index),
        ...parsed.stations?.[index],
      })),
    };
  } catch {
    return emptyPlan();
  }
}

function PracticeLegend() {
  return (
    <div className="practice-legend" aria-label="Drill symbol legend">
      <strong>Legend</strong>
      <div className="practice-legend-grid">
        <span>
          <b>Ⓒ</b> Coach
        </span>
        <span>
          <b>●</b> Player
        </span>
        <span>
          <b>△</b> Defender
        </span>
        <span>
          <b>G</b> Goalie
        </span>
        <span>
          <b>×</b> Pylon
        </span>
        <span>
          <b>•••</b> Pucks
        </span>
        <span>
          <i className="legend-stroke solid" /> Skating
        </span>
        <span>
          <i className="legend-stroke wave">∿∿</i> Puck control
        </span>
        <span>
          <i className="legend-stroke dotted" /> Passing
        </span>
        <span>
          <i className="legend-arrow">→</i> Shooting
        </span>
      </div>
    </div>
  );
}

export function PracticePlannerRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const drills = useMemo(() => loadDrills(), []);
  const [plan, setPlan] = useState<PracticePlan>(loadPlan);
  const drillById = useMemo(
    () => new Map(drills.map((drill) => [drill.id, drill])),
    [drills],
  );
  const totalMinutes = plan.stations.reduce(
    (total, station) => total + station.durationMinutes,
    0,
  );

  useEffect(
    () => localStorage.setItem(STORAGE_KEY, JSON.stringify(plan)),
    [plan],
  );

  useEffect(() => {
    const drillId = searchParams.get("drillId");
    const stationIndex = Number(searchParams.get("station"));
    const drill = drillId ? drillById.get(drillId) : undefined;
    if (!drill || !Number.isInteger(stationIndex) || stationIndex < 0 || stationIndex > 2)
      return;

    setPlan((current) => ({
      ...current,
      stations: current.stations.map((station, index) =>
        index === stationIndex
          ? {
              ...station,
              drillId: drill.id,
              title: drill.title,
              durationMinutes: drill.durationMinutes,
              description: drill.instructions || drill.objective,
              focus: drill.coachingPoints || drill.objective,
            }
          : station,
      ),
    }));
    setSearchParams({}, { replace: true });
  }, [drillById, searchParams, setSearchParams]);

  const updatePlan = (update: Partial<PracticePlan>) =>
    setPlan((current) => ({ ...current, ...update }));

  const updateStation = (index: number, update: Partial<PracticeStation>) =>
    setPlan((current) => ({
      ...current,
      stations: current.stations.map((station, stationIndex) =>
        stationIndex === index ? { ...station, ...update } : station,
      ),
    }));

  const chooseDrill = (index: number, drillId: string) => {
    const drill = drillById.get(drillId);
    if (!drill) {
      updateStation(index, { drillId });
      return;
    }
    updateStation(index, {
      drillId,
      title: drill.title,
      durationMinutes: drill.durationMinutes,
      description: drill.instructions || drill.objective,
      focus: drill.coachingPoints || drill.objective,
    });
  };

  return (
    <main className="practice-planner-page">
      <div className="planner-toolbar no-print">
        <div>
          <p className="eyebrow">Practice planner</p>
          <h1>Three-station practice</h1>
          <p>
            Build the plan from saved drills, then export a clean PDF for the
            rink.
          </p>
        </div>
        <div className="planner-actions">
          <Link className="text-button" to="/drills">
            Drill library
          </Link>
          <button className="primary-button" onClick={() => window.print()}>
            Export PDF
          </button>
        </div>
      </div>

      <section className="practice-document" aria-label="Practice plan">
        <header className="practice-plan-header">
          <div className="practice-summary">
            <div className="practice-title-field">
              <label htmlFor="practice-team">Practice</label>
              <input
                id="practice-team"
                value={plan.team}
                onChange={(event) => updatePlan({ team: event.target.value })}
              />
            </div>
            <div className="practice-meta-grid">
              <label>
                Date
                <input
                  type="date"
                  value={plan.date}
                  onChange={(event) => updatePlan({ date: event.target.value })}
                />
              </label>
              <label>
                Start
                <input
                  type="time"
                  value={plan.startTime}
                  onChange={(event) =>
                    updatePlan({ startTime: event.target.value })
                  }
                />
              </label>
              <span className="practice-total">
                Total
                <strong>{totalMinutes} min</strong>
              </span>
            </div>
            <label className="practice-wide-field">
              Practice theme / goals
              <input
                value={plan.theme}
                onChange={(event) => updatePlan({ theme: event.target.value })}
                placeholder="Skating fundamentals, puck control, teamwork…"
              />
            </label>
            <label className="practice-wide-field">
              Equipment required
              <input
                value={plan.equipment}
                onChange={(event) =>
                  updatePlan({ equipment: event.target.value })
                }
                placeholder="Pucks, cones, tires, goals…"
              />
            </label>
          </div>
          <div className="practice-header-brand" aria-hidden="true">
            <img src="/brand/puckly-logo-white.png" alt="" />
          </div>
          <PracticeLegend />
        </header>

        <div className="practice-stations">
          {plan.stations.map((station, index) => {
            const drill = drillById.get(station.drillId);
            return (
              <article className="practice-station" key={index}>
                <div className="station-heading">
                  <span>0{index + 1}</span>
                  <label className="station-title-field">
                    <strong>Drill:</strong>
                    <input
                      aria-label={`Station ${index + 1} title`}
                      value={station.title}
                      onChange={(event) =>
                        updateStation(index, { title: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Time
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={station.durationMinutes}
                      onChange={(event) =>
                        updateStation(index, {
                          durationMinutes: Math.max(
                            1,
                            Number(event.target.value),
                          ),
                        })
                      }
                    />
                    <span>min</span>
                  </label>
                </div>

                <div className="station-body">
                  <div className="station-rink-column">
                    <label className="station-drill-picker no-print">
                      Saved drill
                      <select
                        value={station.drillId}
                        onChange={(event) =>
                          chooseDrill(index, event.target.value)
                        }
                      >
                        <option value="">Blank half ice</option>
                        {drills.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Link
                      className="station-rink station-rink-link"
                      to={
                        drill
                          ? `/drills/${drill.id}/edit?practiceStation=${index}`
                          : `/drills/new?practiceStation=${index}`
                      }
                      aria-label={`${drill ? "Edit" : "Draw"} station ${index + 1} drill`}
                    >
                      <RinkDiagram
                        template={drill?.rink ?? "half"}
                        rinkSide={drill?.rinkSide ?? "left"}
                        elements={drill?.elements ?? []}
                      />
                      <span className="station-rink-action no-print">
                        {drill ? "Edit drill" : "Draw drill"}
                      </span>
                    </Link>
                  </div>

                  <div className="station-notes">
                    <label>
                      Description
                      <textarea
                        value={station.description}
                        onChange={(event) =>
                          updateStation(index, {
                            description: event.target.value,
                          })
                        }
                        placeholder="Organisation, flow and progressions…"
                      />
                    </label>
                    <label>
                      Focus
                      <textarea
                        value={station.focus}
                        onChange={(event) =>
                          updateStation(index, { focus: event.target.value })
                        }
                        placeholder="Key coaching cues…"
                      />
                    </label>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}
